import React from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Link, useLocation } from 'react-router-dom';

import { QueryResponse } from 'api/queries/getSummaryKpi.query';

import { InformationBoxes } from 'components/common/informationBoxes/informationBoxes';

import { CustomizedTooltip } from 'components/common';
import classes from 'components/widgets/DistributionGraph/components/SummaryRow/SummaryRow.module.scss';
import { shouldShowPlusSign } from 'components/widgets/DistributionGraph/components/SummaryRow/SummaryRow.utils';

export const SummaryRow = ({ api }) => {
    const data = api.getSummaryKpi.data as QueryResponse | undefined;
    const { pathname } = useLocation();
    return data ? (
        <InformationBoxes className={`${classes.row}`}>
            <InformationBoxes.Box>
                <div className={classes.box}>
                    <div className={classes.top}>
                        <div className={classes.smallTitle}>Activity</div>
                        <div className={classes.dataValueContainer}>
                            <div className={classes.title}>
                                <CustomizedTooltip
                                    tooltipContent={data.activity.name}
                                    triggerElement={
                                        data.activity.taskId ? (
                                            <Link
                                                to={{
                                                    pathname: `${pathname}/card-${data.activity.taskId}-1`,
                                                }}
                                                className={classes.activityLink}
                                                data-testid={'activity-link'}
                                            >
                                                <div className={classes.tooltipContentParent}>
                                                    <div className={classes.tooltipContent}>{data.activity.name}</div>
                                                </div>
                                            </Link>
                                        ) : (
                                            data.activity.name
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div>{data.activity.activityId}</div>
                    </div>
                </div>
            </InformationBoxes.Box>
            <InformationBoxes.Box className={classes.plannedFinish}>
                <div className={`${classes.box}`}>
                    <div className={classes.top}>
                        <div className={classes.smallTitle}>Current Planned Finish</div>
                        <div className={classes.title}>
                            <div className={classes.predictionProbability}>
                                <h3>{moment(data.deterministic.pDate).format(constants.formats.date.default)}</h3>
                            </div>
                        </div>
                        <div className={classes.subtitle}>
                            Baseline finish:{' '}
                            {moment(data.deterministic.userDefined.baselineFinish).format(
                                constants.formats.date.default,
                            )}{' '}
                            ({shouldShowPlusSign(data.deterministic.dateVariance)}
                            {data.deterministic.dateVariance}d)
                        </div>
                    </div>
                </div>
            </InformationBoxes.Box>
            <InformationBoxes.Box>
                <div className={classes.box}>
                    <div className={classes.top}>
                        <div className={classes.smallTitle}>
                            {data.aggressive.pValue}% likely by this date (P{data.aggressive.pValue})
                        </div>
                        <div data-testid={'optimistic-forecast'} className={classes.dataValueContainer}>
                            <div className={classes.title}>
                                {moment(data.aggressive.pDate).format(constants.formats.date.default)} (
                                {shouldShowPlusSign(data.aggressive.dateVariance)}
                                {data.aggressive.dateVariance}d)
                            </div>
                        </div>
                    </div>
                </div>
            </InformationBoxes.Box>
            <InformationBoxes.Box className={classes.bestForecast}>
                <div className={classes.box}>
                    <div className={classes.top}>
                        <div className={classes.bestForecastTitle}>
                            {data.realistic.pValue}% likely by this date (P{data.realistic.pValue})
                        </div>
                        <div data-testid={'likely-forecast'} className={classes.dataValueContainer}>
                            <div className={classes.bestForecastSmallTitle}>
                                {moment(data.realistic.pDate).format(constants.formats.date.default)} (
                                {shouldShowPlusSign(data.realistic.dateVariance)}
                                {data.realistic.dateVariance}d)
                            </div>
                        </div>
                    </div>
                </div>
            </InformationBoxes.Box>
            <InformationBoxes.Box>
                <div className={classes.box}>
                    <div className={classes.top}>
                        <div className={classes.smallTitle}>
                            {data.conservative.pValue}% likely by this date (P{data.conservative.pValue})
                        </div>
                        <div data-testid={'pessimistic-forecast'} className={classes.dataValueContainer}>
                            <div className={classes.title}>
                                {moment(data.conservative.pDate).format(constants.formats.date.default)} (
                                {shouldShowPlusSign(data.conservative.dateVariance)}
                                {data.conservative.dateVariance}d)
                            </div>
                        </div>
                    </div>
                </div>
            </InformationBoxes.Box>
        </InformationBoxes>
    ) : null;
};
